import { ToWords } from "to-words";
import { ROUND_OFF_TYPES } from "const";
import * as TerminalHelper from "helpers/TermialHelper";
import ShopsHelper from "./ShopsHelper";

const locale = navigator.language || "en-US";
const toWordsConverter = new ToWords({
  localeCode: locale,
});

const toWordsConverterIn = new ToWords({
  localeCode: "en-IN",
});

export function roundOff(value, precision = 2, ignoreTerminalSetting = false) {
  const settings = TerminalHelper.getTerminalSettings();
  const isThreeFraction = settings?.amountWithThreeFraction;

  if (!ignoreTerminalSetting && isThreeFraction === true) {
    precision = 3;
  }

  return parseFloat(value).toFixed(precision);
}

export function customerAmountFormat(value) {
  const num = Number(value) || 0;
  if (num < 0) {
    return `(cr) ${roundOff(Math.abs(num))}`;
  }
  return roundOff(num);
}

export function quantityRoundOffString(value) {
  const num = Number(value ?? 0);

  if (Number.isInteger(num)) {
    return String(num);
  }

  return num.toFixed(3);
}

export function quantityRoundOff(value) {
  const num = Number(value ?? 0);

  if (Number.isInteger(num)) {
    return num;
  }

  return Math.floor(num * 1000) / 1000;
}

export function amountRoundOff(value, precision = null) {
  const settings = TerminalHelper.getTerminalSettings();
  const isThreeFraction = settings?.amountWithThreeFraction;

  const finalPrecision = (precision ?? isThreeFraction) ? 3 : 2;

  const num = Number(value);
  if (Number.isNaN(num)) return 0;

  // Extended round-off: First round to extended precision (3 decimal places if 2-digit, 4 if 3-digit),
  // then round to final precision. This resolves 0.01 discrepancies from floating point math & discount spreading.
  const extendedPrecision = finalPrecision + 1;
  const extendedFactor = Math.pow(10, extendedPrecision);
  const extendedNum = Math.round((num + Number.EPSILON) * extendedFactor) / extendedFactor;

  const factor = Math.pow(10, finalPrecision);
  return Math.round((extendedNum + Number.EPSILON) * factor) / factor;
}

export function saleAutoRoundOff(amountToRound) {
  const shopProfile = ShopsHelper.getCachedShopProfile();
  if (!shopProfile) return amountRoundOff(amountToRound);

  const precision = shopProfile.automatic_round_off_precision;

  if (precision === undefined || precision === null || precision <= 0) return amountRoundOff(amountToRound);

  const roundType = (shopProfile.automatic_round_off_type || "").toUpperCase();

  let roundedValue;
  switch (roundType) {
    case ROUND_OFF_TYPES.FLOOR:
      roundedValue = Math.floor(amountToRound / precision) * precision;
      break;
    case ROUND_OFF_TYPES.CEIL:
      roundedValue = Math.ceil(amountToRound / precision) * precision;
      break;
    case ROUND_OFF_TYPES.NEAREST:
      roundedValue = Math.round(amountToRound / precision) * precision;
      break;
    default:
      roundedValue = amountToRound;
  }
  return amountRoundOff(roundedValue);
}

export function amountExtendedRoundOff(value, precision = null) {
  const settings = TerminalHelper.getTerminalSettings();
  const isThreeFraction = settings?.amountWithThreeFraction;

  const finalPrecision = (precision ?? isThreeFraction) ? 4 : 3;

  const num = Number(value);
  if (Number.isNaN(num)) return 0;

  const factor = Math.pow(10, finalPrecision);
  return Math.round((num + Number.EPSILON) * factor) / factor;
}

export function toWords(amount) {
  if (amount === null || amount === undefined || (typeof amount === "string" && amount.trim() === "") || isNaN(amount))
    return "";

  const num = Number(amount);
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const shopProfile = ShopsHelper.getCachedShopProfile();
  const currencyName = shopProfile?.currency_name || "";
  const fractionName = shopProfile?.currency_fraction_name || "";

  const integerPart = Math.floor(absNum);
  const fractionalPart = Math.round((absNum - integerPart) * 100);

  try {
    const integerWords = toWordsConverter.convert(integerPart);
    let result = `${integerWords} ${currencyName}`;

    if (fractionalPart > 0) {
      const fractionalWords = toWordsConverter.convert(fractionalPart);
      result += ` and ${fractionalWords} ${fractionName}`;
    }

    return isNegative ? `Minus ${result}` : result;
  } catch (_) {
    try {
      const integerWords = toWordsConverterIn.convert(integerPart);
      let result = `${integerWords} ${currencyName}`;

      if (fractionalPart > 0) {
        const fractionalWords = toWordsConverterIn.convert(fractionalPart);
        result += ` and ${fractionalWords} ${fractionName}`;
      }

      return isNegative ? `Minus ${result}` : result;
    } catch (innerError) {
      return "";
    }
  }
}
